import React from 'react';
import { motion } from 'framer-motion';
import { 
  SiFlutter, SiDart, SiKotlin, SiSwift, SiAndroid, SiApple,
  SiFirebase, SiSupabase, SiPostgresql, SiGraphql,
  SiDocker, SiGit, SiFigma, SiPostman
} from 'react-icons/si';
import { Box, Layers, Network, GitBranch, Workflow, Database, Radio, Wifi, RefreshCw, Zap, Shield, AlertCircle } from 'lucide-react';
import { Card } from './ui/card';

const Skills = () => {
  const skillCategories = [
    {
      title: 'Mobile Development',
      skills: [
        { name: 'Flutter', icon: SiFlutter, color: '#02569B' },
        { name: 'Dart', icon: SiDart, color: '#0175C2' },
        { name: 'Kotlin', icon: SiKotlin, color: '#7F52FF' },
        { name: 'Jetpack Compose', icon: Layers, color: '#4285F4' },
        { name: 'Swift', icon: SiSwift, color: '#FA7343' },
        { name: 'Android Native', icon: SiAndroid, color: '#3DDC84' },
        { name: 'iOS', icon: SiApple, color: '#ffffff' }
      ]
    },
    {
      title: 'Architecture & Patterns',
      skills: [
        { name: 'Clean Architecture', icon: Box, color: '#ffffff' },
        { name: 'SOLID Principles', icon: Layers, color: '#ffffff' },
        { name: 'Modularization', icon: Network, color: '#ffffff' },
        { name: 'System Design', icon: Network, color: '#ffffff' },
        { name: 'BLoC Pattern', icon: GitBranch, color: '#ffffff' },
        { name: 'MVVM', icon: Layers, color: '#ffffff' },
        { name: 'Riverpod', icon: Workflow, color: '#ffffff' }
      ]
    },
    {
      title: 'Backend & Services',
      skills: [
        { name: 'Firebase', icon: SiFirebase, color: '#FFCA28' },
        { name: 'Supabase', icon: SiSupabase, color: '#3ECF8E' },
        { name: 'PostgreSQL', icon: SiPostgresql, color: '#336791' },
        { name: 'REST APIs', icon: Radio, color: '#ffffff' },
        { name: 'WebSockets', icon: Wifi, color: '#ffffff' },
        { name: 'GraphQL', icon: SiGraphql, color: '#E10098' }
      ]
    },
    {
      title: 'DevOps & Tools',
      skills: [
        { name: 'Docker', icon: SiDocker, color: '#2496ED' },
        { name: 'Git', icon: SiGit, color: '#F05032' },
        { name: 'CI/CD', icon: RefreshCw, color: '#ffffff' },
        { name: 'Fastlane', icon: Zap, color: '#00F200' },
        { name: 'Crashlytics', icon: AlertCircle, color: '#FFCA28' },
        { name: 'Sentry', icon: Shield, color: '#362D59' },
        { name: 'Postman', icon: SiPostman, color: '#FF6C37' },
        { name: 'Figma', icon: SiFigma, color: '#F24E1E' }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="skills" className="section-padding bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Technical Expertise</h2>
          <p className="section-subtitle mx-auto">
            Full-stack mobile development with focus on architecture and scalability
          </p>
        </motion.div>

        {/* Skills Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {skillCategories.map((category, catIndex) => (
            <motion.div key={category.title} variants={itemVariants}>
              <Card className="bg-[#1a1a1a] border-[#2a2a2a] hover:border-[#404040] p-8 transition-all duration-300 h-full">
                <h3 className="text-2xl font-semibold mb-8 text-white">{category.title}</h3>
                <div className="grid grid-cols-2 gap-4">
                  {category.skills.map((skill) => {
                    const Icon = skill.icon;
                    return (
                      <motion.div
                        key={skill.name}
                        whileHover={{ scale: 1.05, y: -2 }}
                        className="flex items-center gap-3 p-4 bg-[#0a0a0a] rounded-xl hover:bg-[#2a2a2a] transition-all cursor-pointer group"
                      >
                        <Icon size={24} style={{ color: skill.color }} className="flex-shrink-0 group-hover:scale-110 transition-transform" />
                        <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                          {skill.name}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
